import isMobile from "ismobilejs";
import mode from "./mode";
import { initURLS, getCategoryId, getCategoryName } from "./urls";

const orientation = window.matchMedia("(orientation: portrait)").matches ? "portrait" : "landscape";
const direction = document.querySelector("html").getAttribute("dir") || "ltr";
const clientAppConfig = window.zara?.viewPayload?.clientAppConfig;
const location =
  clientAppConfig?.domains &&
  clientAppConfig.domains.desktop &&
  clientAppConfig.domains.desktop.static &&
  clientAppConfig.domains.desktop.static.base
    ? clientAppConfig.domains.desktop.static.base
    : "";

const debugMode = /\bdebugMode\b/.test(document.cookie);
const isShowroomApp = Boolean(window.navigator.userAgent.match("ZaraPressApp"));

const getChannels = channel => {
  let parsedChannel = /spa|web/.test(channel) ? "desktop" : channel;
  const channels = {
    desktop: false,
    mobile: false,
    standalone: false,
    apps: false,
    minip: false,
  };
  if (parsedChannel) {
    channels[parsedChannel] = true;
  }
  return { channel: parsedChannel, channels };
};

const getTimestamp = () => {
  const url = document.currentScript?.src;
  if (url) {
    const urlParams = new URLSearchParams(url.split("?")[1]);
    const tsValue = urlParams.get("ts");
    return tsValue;
  }
  return 2222222222222;
};

const timestamp = getTimestamp();

let g = {
  location,
  basePath: `${window.location.origin}${isShowroomApp ? "/integration/pressapi/" : ""}`,
  prestage: location.indexOf("prestage") > -1 && debugMode,
  developerMode: debugMode,
  categories: {},
  request: 0,
  config: {},
  timestamp: 2222222222222, // will be overrided
  lastStaticSyncTimestamp: 2222222222222, // will be overrided
  channels: {
    desktop: false,
    mobile: false,
    standalone: false,
    apps: false,
    minip: false,
  },
  isTablet: isMobile(window.navigator).tablet,
  isShowroomApp,
  deviceOrientation: orientation,
  allowedWidths: [1920, 1024, 560, 400, 135],
  products: {},
  supportedLocales: [],
  categoryName: getCategoryName(),
  categoryId: getCategoryId(),
  predicates: {},
  enhancementsLoaded: [],
  sharingMethods: [],
  eagerMode: /eager/.test(window.location.href),
  editMode: /editMode=([^;]+)/.test(document.cookie) && RegExp.$1,
  direction,
  joinLife: [
    "I2020-SUSTAINABILITY",
    "I2020-SUSTAINABILITY-COLECCION",
    "S2020-SUSTAINABILITY",
    "S2020-SUSTAINABILITY-COLECCION",
    "V2020-SUSTAINABILITY",
    "V2020-SUSTAINABILITY-COLECCION",
    "W2020-SUSTAINABILITY",
    "W2020-SUSTAINABILITY-COLECCION",
    "I2020R-SUSTAINABILITY",
    "I2020R-SUSTAINABILITY-COLECCION",
    "V2021-SUSTAINABILITY",
    "V2021-SUSTAINABILITY-COLECCION",
    "S2020R-SUSTAINABILITY",
    "S2020R-SUSTAINABILITY-COLECCION",
    "W2021-SUSTAINABILITY",
    "W2021-SUSTAINABILITY-COLECCION",
  ],
  // regularSection 0 is the defaultValue
  regularSections: ["WOMAN", "MAN", "KID", "HOME"],
  // order matters
  footerItems: [
    ".layout-footer-marketing",
    ".newsletter-wrapper",
    ".terms-cont",
    "#mkt-footer",
    "#newsletter-subscribe-widget",
    "#network-footer",
    ".privacy",
    "#legal-footer",
    ".qr-list",
  ],
};

if (clientAppConfig) {
  g = {
    allowedWidths: [1920, 1024, 560, 400, 135],
    baseImagesUrl: clientAppConfig.imageBaseUrl,
    basePath: `${window.location.origin}${isShowroomApp ? "/integration/pressapi/" : ""}`,
    categories: {},
    categoryId: getCategoryId(),
    categoryName: getCategoryName(),
    catalogId: clientAppConfig.store.catalogId,
    ...getChannels(clientAppConfig.channel),
    config: {},
    channel: document.querySelector("html")?.classList.contains("apps") ? "apps" : clientAppConfig.channel,
    countryCode: clientAppConfig.store.countryCode,
    currencyCode: clientAppConfig.formatterConfig.currencyCode,
    currencyDecimals: clientAppConfig.formatterConfig.currencyDecimals,
    currencyFormat: clientAppConfig.formatterConfig.currencyFormat,
    currencySymbol: clientAppConfig.formatterConfig.currencySymbol,
    decimalsSeparator: clientAppConfig.formatterConfig.formats.number.decimalSeparator,
    debugMode: debugMode,
    developerMode: debugMode,
    deviceOrientation: orientation,
    direction,
    eagerMode: /eager/.test(window.location.href),
    editMode: /editMode=([^;]+)/.test(document.cookie) && RegExp.$1,
    edge: /edge=(.*?);/.test(document.cookie) && RegExp.$1,
    enhancementsLoaded: [],
    footerItems: [
      ".layout-footer-marketing",
      ".newsletter-wrapper",
      ".terms-cont",
      "#mkt-footer",
      "#newsletter-subscribe-widget",
      "#network-footer",
      ".privacy",
      "#legal-footer",
      ".qr-list",
    ],
    imageDir: clientAppConfig.imageBaseUrl,
    isShowroomApp,
    isTablet: isMobile(window.navigator).tablet,
    joinLife: [
      "I2020-SUSTAINABILITY",
      "I2020-SUSTAINABILITY-COLECCION",
      "S2020-SUSTAINABILITY",
      "S2020-SUSTAINABILITY-COLECCION",
      "V2020-SUSTAINABILITY",
      "V2020-SUSTAINABILITY-COLECCION",
      "W2020-SUSTAINABILITY",
      "W2020-SUSTAINABILITY-COLECCION",
      "I2020R-SUSTAINABILITY",
      "I2020R-SUSTAINABILITY-COLECCION",
      "V2021-SUSTAINABILITY",
      "V2021-SUSTAINABILITY-COLECCION",
      "S2020R-SUSTAINABILITY",
      "S2020R-SUSTAINABILITY-COLECCION",
      "W2021-SUSTAINABILITY",
      "W2021-SUSTAINABILITY-COLECCION",
    ],
    languageCode: clientAppConfig.store.searchLang,
    langCode: clientAppConfig.langCode,
    langId: clientAppConfig.langId,
    lastStaticSyncTimestamp: timestamp,
    locale: clientAppConfig.locale,
    localeEdge: clientAppConfig.store.searchLang.replace("_", "-"),
    location,
    mainStoreLangCode: clientAppConfig.storeCountryCode,
    predicates: {},
    prestage: location.indexOf("prestage") > -1 && debugMode,
    privacyPolicy: clientAppConfig.store.privacyUrl,
    production: /zara.(cn|com)/.test(window.location.href),
    products: {},
    regularSection: 0,
    regularSections: ["WOMAN", "MAN", "KID", "HOME"],
    request: 0,
    sharingMethods: [],
    staticContent: clientAppConfig.imageBaseUrl.replace("photos", "static"),
    storeCode: clientAppConfig.storeCode,
    storeCountryCode: clientAppConfig.storeCountryCode,
    storeId: clientAppConfig.store.id,
    supportedLocales: clientAppConfig.store.supportedLanguages,
    timestamp,
    videoContent: clientAppConfig.videoBaseUrl,
  };
}

if (g.developerMode || !g.production) {
  window.__mkt_g = g;
  mode.parse();
  if (mode.query.globals) {
    Object.assign(g, JSON.parse(mode.query.globals));
  }
}

g.setGlobals = globals => {
  const exposedGlobals = {
    ...g,
    ...globals,
  };
  const { channels } = getChannels(exposedGlobals.channel || clientAppConfig?.channel || "standalone");
  exposedGlobals.urls = initURLS(channels);
  exposedGlobals.locale = exposedGlobals.locale?.replace(/zh_TW/, "zt_TW");
  exposedGlobals.channels = channels;
  exposedGlobals.channel =
    exposedGlobals.channel !== "minip" && document.querySelector("html")?.classList.contains("apps") ? "apps" : exposedGlobals.channel;
  Object.assign(g, exposedGlobals);
  window._mkt = {
    g,
  };
};

g.changeLanguage = language => {
  Object.assign(g, language);
  const htmlNode = document.querySelector("html");
  htmlNode.setAttribute("dir", language.direction);
  htmlNode.setAttribute("lang", language.localeEdge);
};

export default g;
